import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  getProviderRating,
  hasPermissionToViewData,
  isHaulerApproved,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { profileImageServeUrl } from '../../util/api';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  InlineFetchError,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  CustomExtendedDataSection,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import { toggleFavoriteHauler } from './ProfilePage.duck';
import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

// Haul.Taxi — same-origin serve URL for a hauler's public profile image (logo / before-after item),
// cache-busted with a short token from the object key so a replaced image doesn't show stale within the
// serve response's cache window. The serve route derives the key server-side from userId + kind (+ idx).
const publicImageUrl = (userId, kind, idx, key) => {
  const base = profileImageServeUrl(userId, kind, idx);
  const v = typeof key === 'string' ? key.slice(-12) : '';
  return v ? `${base}&v=${encodeURIComponent(v)}` : base;
};

// Haul.Taxi — one-line vehicle summary for the hauler header: the human label for the vehicleType
// enum (resolved from the code-owned user-field config) joined with the free-text make/model.
export const getVehicleSummary = (publicData, userFieldConfig) => {
  const vehicleType = publicData?.vehicleType;
  const makeModel = publicData?.vehicleMakeModel;
  const typeConfig = (userFieldConfig || []).find(f => f?.key === 'vehicleType');
  const typeLabel =
    typeConfig?.enumOptions?.find(o => `${o.option}` === vehicleType)?.label || null;
  const makeModelStr = typeof makeModel === 'string' && makeModel.trim() ? makeModel.trim() : null;
  const parts = [typeLabel, makeModelStr].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

// Haul.Taxi — the verified-hauler trust block shown in the profile header for approved haulers.
// Reuses the branded VERIFIED badge pattern from the compare-bids dashboard (JobBidsPage).
// Tier-1 trust signals are split into per-signal badges — each backed by real PUBLIC data:
//   • "ID verified"      ← metadata.identityVerificationStatus === 'verified' (Stripe Identity)
//   • "Verified hauler"  ← metadata.haulerApproved (operator KYC review)
// plus an "On Haul since {year}" line from the account createdAt. We deliberately surface NO
// insurance/background badges — there is no public data backing those yet.
// Haul.Taxi — a single derived trust stat, formatted as a % where relevant. Rates are only ever passed
// here when non-null (the worker gates them behind a minimum sample), so a null just renders nothing.
const HaulerStat = ({ id, rate, count, intl }) => {
  if (rate != null) {
    const pct = intl.formatNumber(rate, { style: 'percent', maximumFractionDigits: 0 });
    return (
      <span className={css.haulerStat}>
        <FormattedMessage id={id} values={{ rate: pct }} />
      </span>
    );
  }
  if (typeof count === 'number' && count > 0) {
    return (
      <span className={css.haulerStat}>
        <FormattedMessage id={id} values={{ count }} />
      </span>
    );
  }
  return null;
};

// Haul.Taxi — bucket a median first-reply time into a COARSE duration: minutes below an hour, whole
// hours below a day, then days. The underlying figure is a median over a handful of conversations, so
// "replies in 47 minutes" would imply a precision it does not have. Returns null for anything
// unusable, which is how the row self-suppresses when the worker withheld the value.
//
// Exported for direct testing: rendering through FormattedMessage cannot distinguish the buckets,
// because the test intl returns the message id and the duration is passed as a value, not as text.
export const responseTimeBucket = minutes => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes < 0) {
    return null;
  }
  if (minutes < 60) {
    // Never "0 min" — a sub-minute median is still "1 min", not instantaneous.
    return { id: 'ProfilePage.responseTimeMinutes', count: Math.max(1, Math.round(minutes)) };
  }
  if (minutes < 60 * 24) {
    return { id: 'ProfilePage.responseTimeHours', count: Math.round(minutes / 60) };
  }
  return { id: 'ProfilePage.responseTimeDays', count: Math.round(minutes / (60 * 24)) };
};

// The hauler's typical first-reply time, derived by the worker from real message timestamps
// (metadata.haulerStats.responseTimeMinutes — operator-only-writable, so a hauler can't fake it).
const HaulerResponseTime = ({ minutes, intl }) => {
  const bucket = responseTimeBucket(minutes);
  if (!bucket) {
    return null;
  }
  const duration = intl.formatMessage({ id: bucket.id }, { count: bucket.count });
  return (
    <span className={css.haulerStat}>
      {/* "about" is load-bearing: nearest-unit rounding can round DOWN (89 min reads as "1 hr"),
          so the copy must not promise a precision — or a speed — the median doesn't support. */}
      <FormattedMessage id="ProfilePage.responseTime" values={{ duration }} />
    </span>
  );
};

// Haul.Taxi — the five customer-review sub-score dimensions, in the same order they're collected in
// the review form (TransactionPage CUSTOMER_REVIEW_DIMENSIONS).
const REVIEW_DIMENSION_KEYS = ['communication', 'onTime', 'professionalism', 'cleanup', 'value'];

// One labelled 0-5 average row, drawn as a proportional bar. Self-suppresses when the dimension has no
// average (older/partial reviews). The whole row is a role="img" with a clean aria-label so AT reads
// "Communication: 4.8 out of 5" rather than the bar markup.
const ReviewDimensionRow = ({ id, dim, intl }) => {
  const avg = dim?.avg;
  if (avg == null) {
    return null;
  }
  const label = intl.formatMessage({ id });
  const avgStr = intl.formatNumber(avg, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const rowLabel = intl.formatMessage(
    { id: 'ProfilePage.reviewDimension.rowLabel' },
    { label, average: avgStr }
  );
  return (
    <div className={css.reviewDimensionRow} role="img" aria-label={rowLabel}>
      <span className={css.reviewDimensionLabel} aria-hidden="true">
        {label}
      </span>
      <span className={css.reviewDimensionBar} aria-hidden="true">
        <span
          className={css.reviewDimensionBarFill}
          style={{ width: `${Math.max(0, Math.min(100, (avg / 5) * 100))}%` }}
        />
      </span>
      <span className={css.reviewDimensionAvg} aria-hidden="true">
        {avgStr}
      </span>
    </div>
  );
};

// Haul.Taxi — the tamper-proof per-dimension customer-review breakdown (operator-only
// metadata.reviewDimensions, written by the sidecar worker). Rendered only when there is at least one
// scored review; a hauler with none (or older reviews lacking sub-scores) renders nothing.
export const ReviewDimensions = props => {
  const { reviewDimensions, intl } = props;
  const dims = reviewDimensions || {};
  const count = typeof dims.count === 'number' ? dims.count : 0;
  if (count <= 0) {
    return null;
  }
  const wouldHireAgainRate = dims.wouldHireAgainRate;
  return (
    <div className={css.reviewDimensions}>
      <span className={css.reviewDimensionsTitle}>
        <FormattedMessage id="ProfilePage.reviewDimension.title" />
      </span>
      {REVIEW_DIMENSION_KEYS.map(key => (
        <ReviewDimensionRow
          key={key}
          id={`ProfilePage.reviewDimension.${key}`}
          dim={dims[key]}
          intl={intl}
        />
      ))}
      {wouldHireAgainRate != null ? (
        <span className={css.reviewDimensionWouldHire}>
          <FormattedMessage
            id="ProfilePage.reviewDimension.wouldHireAgain"
            values={{
              rate: intl.formatNumber(wouldHireAgainRate, {
                style: 'percent',
                maximumFractionDigits: 0,
              }),
            }}
          />
        </span>
      ) : null}
      <span className={css.reviewDimensionsCaption}>
        <FormattedMessage id="ProfilePage.reviewDimension.basedOn" values={{ count }} />
      </span>
    </div>
  );
};

export const VerifiedHaulerInfo = props => {
  const {
    rating,
    vehicle,
    reviewsError,
    identityVerified,
    memberSinceYear,
    haulerStats,
    reviewDimensions,
    intl,
  } = props;
  const stats = haulerStats || {};
  // Whether the derived track-record block has anything to show — jobs completed (>0), either rate,
  // or a published response time (all non-null). A brand-new hauler has none of these, so the whole
  // block is omitted. Every published signal must be listed here, or a hauler whose ONLY signal is
  // that one would render an empty block.
  const hasAnyStat =
    (typeof stats.jobsCompleted === 'number' && stats.jobsCompleted > 0) ||
    stats.repeatCustomerRate != null ||
    stats.cancellationRate != null ||
    stats.responseTimeMinutes != null;
  const hasRating = !!rating && rating.count > 0;
  // Localize the average — the decimal separator differs by locale ("4.5" vs "4,5").
  const averageStr = hasRating
    ? intl.formatNumber(rating.average, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;
  const ratingLabel = hasRating
    ? intl.formatMessage(
        { id: 'ProfilePage.haulerRatingLabel' },
        { average: averageStr, count: rating.count }
      )
    : undefined;
  // Only assert a rating / "new hauler" claim when the reviews query actually succeeded. On a fetch
  // error props.reviews is an empty [] indistinguishable from a genuine zero, so we omit the line
  // rather than mislabel an established hauler as "New hauler" (the badge + vehicle still show).
  const showRatingLine = !reviewsError;
  return (
    <div className={css.haulerInfo}>
      <div className={css.badges}>
        {identityVerified ? (
          <span
            className={css.idVerifiedBadge}
            title={intl.formatMessage({ id: 'ProfilePage.identityVerifiedTitle' })}
          >
            <FormattedMessage id="ProfilePage.identityVerified" />
          </span>
        ) : null}
        <span
          className={css.verifiedBadge}
          title={intl.formatMessage({ id: 'ProfilePage.verifiedHaulerTitle' })}
        >
          <FormattedMessage id="ProfilePage.verifiedHauler" />
        </span>
      </div>
      {memberSinceYear ? (
        <span className={css.memberSince}>
          <FormattedMessage id="ProfilePage.memberSince" values={{ year: memberSinceYear }} />
        </span>
      ) : null}
      {showRatingLine && hasRating ? (
        // role="img" + aria-label: a plain <span> has role "generic", which doesn't expose an
        // accessible name, so AT would read the literal ★ glyph. role="img" makes the children
        // presentational and announces the clean label instead.
        <span className={css.haulerRating} role="img" aria-label={ratingLabel}>
          <FormattedMessage
            id="ProfilePage.haulerRating"
            values={{ average: averageStr, count: rating.count }}
          />
        </span>
      ) : showRatingLine ? (
        <span className={css.haulerNew}>
          <FormattedMessage id="ProfilePage.haulerNoReviews" />
        </span>
      ) : null}
      {vehicle ? <span className={css.haulerVehicle}>{vehicle}</span> : null}
      {/* Derived, tamper-proof track record (operator-only metadata.haulerStats). Each row is
          self-gating: jobs-completed shows only above zero; the rates only when the sample cleared
          the worker's minimum (i.e. non-null). Omitted entirely for a brand-new hauler. */}
      {hasAnyStat ? (
        <div className={css.haulerStats}>
          <HaulerStat id="ProfilePage.jobsCompleted" count={stats.jobsCompleted} intl={intl} />
          <HaulerStat
            id="ProfilePage.repeatCustomerRate"
            rate={stats.repeatCustomerRate}
            intl={intl}
          />
          <HaulerStat id="ProfilePage.cancellationRate" rate={stats.cancellationRate} intl={intl} />
          <HaulerResponseTime minutes={stats.responseTimeMinutes} intl={intl} />
        </div>
      ) : null}
      {/* Derived, tamper-proof per-dimension customer-review breakdown (operator-only
          metadata.reviewDimensions). Self-suppresses when there are no scored reviews. */}
      <ReviewDimensions reviewDimensions={reviewDimensions} intl={intl} />
    </div>
  );
};

export const AsideContent = props => {
  const {
    user,
    displayName,
    showLinkToProfileSettingsPage,
    isApprovedHauler,
    companyLogoUrl,
    rating,
    vehicle,
    reviewsError,
    identityVerified,
    memberSinceYear,
    haulerStats,
    reviewDimensions,
    showFavoriteButton,
    isFavorite,
    onToggleFavorite,
    intl,
  } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      {isApprovedHauler && companyLogoUrl ? (
        <img
          className={css.companyLogo}
          src={companyLogoUrl}
          alt={intl.formatMessage({ id: 'ProfilePage.companyLogoAlt' }, { name: displayName })}
        />
      ) : null}
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isApprovedHauler ? (
        <VerifiedHaulerInfo
          rating={rating}
          vehicle={vehicle}
          reviewsError={reviewsError}
          identityVerified={identityVerified}
          memberSinceYear={memberSinceYear}
          haulerStats={haulerStats}
          reviewDimensions={reviewDimensions}
          intl={intl}
        />
      ) : null}
      {showFavoriteButton ? (
        <button
          type="button"
          className={classNames(css.favoriteButton, {
            [css.favoriteButtonActive]: isFavorite,
          })}
          onClick={onToggleFavorite}
          aria-pressed={isFavorite}
        >
          <FormattedMessage id={isFavorite ? 'ProfilePage.favorited' : 'ProfilePage.favorite'} />
        </button>
      ) : null}
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <InlineFetchError
      messageId="ProfilePage.loadingReviewsFailed"
      className={css.reviewsErrorSpacing}
    />
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      {/* Error OR list — never both, so the branded empty state can't surface under an error. */}
      {queryReviewsError ? (
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      ) : (
        <Reviews reviews={reviewsOfProvider} />
      )}
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      {queryReviewsError ? (
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      ) : (
        <Reviews reviews={reviewsOfCustomer} />
      )}
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        {/* Error OR list — never both, so the branded empty state can't surface under an error. */}
        {queryReviewsError ? (
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        ) : isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

// Cap on the rendered gallery. Keep in sync with server/api-util/profileImages.js (MAX_GALLERY), which
// enforces it on upload and on serve: publicData is owner-writable through the raw Marketplace API, so an
// over-long beforeAfter array is possible even though confirm-upload refuses to create one.
const MAX_GALLERY = 8;

// Haul.Taxi — a hauler's before/after photo gallery (world-readable publicData.beforeAfter). Each item
// is streamed through the same-origin serve proxy by (userId, 'gallery', idx); the idx is the item's real
// array position so it maps to the right key server-side. Null-safe: renders nothing without images.
export const BeforeAfterGallery = props => {
  const { userId, gallery, intl } = props;
  const items = (Array.isArray(gallery) ? gallery : [])
    .slice(0, MAX_GALLERY)
    .map((entry, idx) => ({ entry, idx }))
    .filter(({ entry }) => entry?.key);
  if (!userId || items.length === 0) {
    return null;
  }
  return (
    <div className={css.beforeAfterSection}>
      <H4 as="h2" className={css.beforeAfterTitle}>
        <FormattedMessage id="ProfilePage.beforeAfterTitle" />
      </H4>
      <ul className={css.beforeAfterGrid}>
        {items.map(({ entry, idx }) => (
          <li className={css.beforeAfterItem} key={entry.key}>
            <img
              className={css.beforeAfterImage}
              src={publicImageUrl(userId, 'gallery', idx, entry.key)}
              alt={intl.formatMessage(
                { id: 'ProfilePage.beforeAfterImageAlt' },
                { index: idx + 1 }
              )}
              loading="lazy"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    galleryUserId,
    gallery,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      <BeforeAfterGallery userId={galleryUserId} gallery={gallery} intl={intl} />

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    isFavorite,
    onToggleFavorite,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;

  // Haul.Taxi — verified-hauler trust block in the profile header. Shown only when the viewed user
  // is an operator-approved hauler (metadata.haulerApproved). The badge belongs on the hauler's own
  // profile; customers (who author job listings) never get it.
  // Double-gate: also require publicData.userType === 'hauler' so an operator misflag on a customer
  // profile (accidentally setting haulerApproved: true) doesn't surface the badge.
  const profileUserIsApprovedHauler =
    isHaulerApproved(profileUser) &&
    profileUser?.attributes?.profile?.publicData?.userType === 'hauler';
  const haulerRating = profileUserIsApprovedHauler ? getProviderRating(props.reviews) : null;
  const haulerVehicle = profileUserIsApprovedHauler
    ? getVehicleSummary(publicData, userFields)
    : null;

  // Haul.Taxi — Tier-1 trust signals surfaced in the hauler header, both backed by PUBLIC data:
  // identity verification status (Stripe Identity) and the account join year (createdAt).
  const identityVerified = metadata?.identityVerificationStatus === 'verified';
  const createdAt = profileUser?.attributes?.createdAt;
  const memberSinceYear = createdAt ? new Date(createdAt).getFullYear() : null;

  // Haul.Taxi — derived, tamper-proof trust stats written by the sidecar worker into the operator-only
  // metadata.haulerStats (a hauler can't fake them). Only surfaced for an approved hauler; the rates are
  // already null below the worker's minimum sample, so the header self-suppresses them.
  const haulerStats = profileUserIsApprovedHauler ? metadata?.haulerStats : null;

  // Haul.Taxi — derived, tamper-proof per-dimension customer-review averages, written by the sidecar
  // worker into the operator-only metadata.reviewDimensions. Only surfaced for an approved hauler; the
  // block self-suppresses below one scored review (count 0).
  const haulerReviewDimensions = profileUserIsApprovedHauler ? metadata?.reviewDimensions : null;

  // Haul.Taxi — hauler PUBLIC profile images (world-readable publicData.companyLogo + beforeAfter),
  // stored in R2 and streamed through the same-origin serve proxy. Surfaced only for an approved hauler;
  // null-safe when absent. The gallery array is passed straight through so the serve idx matches the key.
  const profileUserId = profileUser?.id?.uuid;
  const companyLogo = profileUserIsApprovedHauler ? publicData?.companyLogo : null;
  const companyLogoUrl =
    companyLogo?.key && profileUserId
      ? publicImageUrl(profileUserId, 'logo', undefined, companyLogo.key)
      : null;
  const beforeAfterGallery = profileUserIsApprovedHauler ? publicData?.beforeAfter : null;

  // Haul.Taxi — favorite toggle: shown only to a logged-in viewer looking at an approved hauler's
  // profile that is NOT their own. Ownership is derived from the resolved entities (not pathParams)
  // so it holds regardless of how the route was reached. Gate on `mounted` so SSR (no auth) and the
  // first client render agree, avoiding a hydration mismatch (same pattern as the edit-profile link).
  const isOwnProfile =
    !!currentUser?.id && !!profileUser?.id && currentUser.id.uuid === profileUser.id.uuid;
  const showFavoriteButton =
    mounted && !!currentUser && !isOwnProfile && profileUserIsApprovedHauler;
  const handleToggleFavorite = () => onToggleFavorite(profileUser?.id?.uuid);

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && (isNotFoundError(userShowError) || userShowError?.status === 400)) {
    // 404 = no such user; 400 = a malformed/invalid user id in the URL (e.g. /u/not-a-uuid). The
    // user-show request for this route takes only ONE variable input — the :id path param (its
    // include/fields/image-variant options are fixed, valid constants) — so a 400 here can only mean
    // a bad id. Both cases mean "this profile can't be shown", so render the branded 404 page instead
    // of falling through to the generic "something went wrong" error inside the profile layout.
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            displayName={displayName}
            isApprovedHauler={profileUserIsApprovedHauler}
            companyLogoUrl={companyLogoUrl}
            rating={haulerRating}
            vehicle={haulerVehicle}
            reviewsError={props.queryReviewsError}
            identityVerified={identityVerified}
            memberSinceYear={memberSinceYear}
            haulerStats={haulerStats}
            reviewDimensions={haulerReviewDimensions}
            showFavoriteButton={showFavoriteButton}
            isFavorite={isFavorite}
            onToggleFavorite={handleToggleFavorite}
            intl={intl}
          />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          galleryUserId={profileUserIsApprovedHauler ? profileUserId : null}
          gallery={beforeAfterGallery}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  // Haul.Taxi — whether the viewed hauler is in the current user's private favorites list.
  // Derived straight from redux so it re-renders after toggleFavoriteHauler → updateProfile →
  // setCurrentUser refreshes the currentUser entity.
  const favoriteHaulers = currentUser?.attributes?.profile?.privateData?.favoriteHaulers || [];
  const isFavorite = userId?.uuid ? favoriteHaulers.includes(userId.uuid) : false;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    isFavorite,
  };
};

const mapDispatchToProps = dispatch => ({
  onToggleFavorite: haulerId => dispatch(toggleFavoriteHauler(haulerId)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePageComponent);

export default ProfilePage;
